import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Header } from './components/Header';
import { Pda, PdaScanEvent } from './components/Pda';
import { Input, Button, ImageViewer, Tabs, Tag, Divider, Toast } from 'antd-mobile';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { ScanningOutline } from 'antd-mobile-icons';
import { gql, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { isValidBarCode, showBarCodeError, isBagNumber, isTtBagNumber } from '../../utils/BarCodeUtil';
import { useBagQueryDegradeEnabled } from '../../graphql/queries/commonConfigQueries';

// 根据扫描输入查询订单或包牌（后端 BarCodeUtil.isAnyBagNumber 判断分支）
// 包裹分支：getOrderByScanInput 直接返回完整订单详情（与 getOrderDetailForCentral 同源），一次拉齐详情 + 事件轨迹
const GET_ORDER_OR_BAG_BY_SCAN_INPUT = gql`
  query GetOrderOrBagByScanInput($scanValue: String!) {
    getOrderOrBagByScanInput(scanValue: $scanValue) {
      getOrderByScanInput {
        orderId
        bagBatchNumber
        orderWithAddresses {
          order {
            id
            trackingNumber
            sortingCode
            returnSortingCode
            overallStatus
            nextAction
            deliveryAttempts
            exceptionReasonCode
            isAbnormal
            channelId
            exceptionImageS3UrlsJson
            podImageS3UrlsJson
            dsp {
              id
              name
            }
          }
        }
        orderWms {
          ttSortingCode
          sortingRule
          temuSortingCode
        }
        deliveryInfo {
          taskDate
          routeName
          stopNumber
          driverName
        }
        orderEvents {
          eventId
          time
          type
          title
          details
          failReason
          nextAction
          exceptionReasonCode
          exceptionSubReasons
          channelId
          channelApiVersion
          isDestHubArrived
          scStages
          isFirstLmArrival
          messageKey
          originalEventType
          operationSource
          appointmentNumber
          driverAccountId
          driverName
          driverPhone
          dspId
          dspName
          operatorName
          operatorPhone
          operatorRole
          operatorWarehouseName
          operatorDspName
          pickupHubName
          sortingMachineCode
          sortingMachineType
          currentWarehouseName
          bagNumber
          clientCartonNo
          presortCode
          originalScanAction
          bagSortingCode
          scanSource
          bagTaskName
          licensePlate
          sealNo
          deliveryAttempts
          deliveryDistance
          latitude
          longitude
          weightKg
          notes
          deliveryMethod
          revokeReason
          departureEventType
          carrierName
          fromBagNumber
          toBagNumber
          reason
          oldSortingRule
          newSortingRule
          imageS3UrlsJson
          podComment
          previousStatus
          newStatus
          previousReviewNotes
          oldRecipientAddress {
            id
            fullName
            addressLine1
            addressLine2
            city
            state
            zipcode
            phoneNumber
            email
            countryCode
            extension
            company
            instructions
          }
          newRecipientAddress {
            id
            fullName
            addressLine1
            addressLine2
            city
            state
            zipcode
            phoneNumber
            email
            countryCode
            extension
            company
            instructions
          }
        }
      }
      getBagByScanInput {
        bagNumber
        sortingCode
        businessType
        sortingRule
        promptSortingRule
        status
        creationWarehouseName
        createdAt
        pastParcelCount
        currentParcelCount
        shipmentHistory {
          id
          kind
          occurredAt
          operatorName
          operatorRole
          warehouseName
          parcelCount
          oldSortingRule
          newSortingRule
          failMessageKey
          originalScanType
        }
      }
    }
  }
`;

// Channel查询：根据channelId获取渠道信息
const GET_CHANNEL = gql`
  query ChannelQuery($id: Long!) {
    getChannel(id: $id) {
      id
      channelName
    }
  }
`;

// 按异常原因码查询异常字典，获取多语言 secondaryType 作为 Reason 展示
const GET_EXCEPTION_DICTIONARY_BY_REASON_CODE = gql`
  query ExceptionDictionaryQuery($exceptionReasonCode: String!) {
    getExceptionDictionaryByReasonCode(exceptionReasonCode: $exceptionReasonCode) {
      exceptionReasonCode
      secondaryTypeZh
      secondaryTypeEn
      secondaryTypeEs
    }
  }
`;


// 地址对象 → 多行字符串（与 desktop-web CustomParcelsTimeline.formatAddress 一致）
// 用于 OrderEventDataRecipientAddressChanged 事件的旧/新地址展示
const formatAddress = (address: any): string => {
  if (!address) return '';
  const parts = [
    address.addressLine1,
    address.addressLine2,
    `${address.city ?? ''}, ${address.state ?? ''}, ${address.zipcode ?? ''}, US`,
  ].filter(Boolean);
  return parts.join('\n');
};

// 包牌/订单详情表格行：第一列 label 左对齐 + 窄，第二列 value 居中；
// 默认 whitespace-nowrap（保持运单号/包牌号等短字段单行紧凑）；
// 长字段（司机名、DSP 名等可能超长的自由文本）传 wrap 允许换行，避免整表被撑出横向滚动条
const BagDetailRow: React.FC<{ label: string; value: string; wrap?: boolean }> = ({ label, value, wrap }) => (
  <tr>
    <td className="border border-gray-200 text-gray-500 text-left py-1 px-2 w-1/3">{label}</td>
    <td className={`border border-gray-200 font-medium text-center py-1 px-2 ${wrap ? 'break-all' : 'whitespace-nowrap'}`}>{value}</td>
  </tr>
);

// 包牌状态 int → i18n 文案
const formatBagStatus = (status: number | null | undefined, t: any): string => {
  if (status === null || status === undefined) return '-';
  const keyMap: Record<number, string> = {
    0: 'notUsed',
    1: 'used',
    2: 'discarded',
    3: 'handover',
    4: 'closed',
  };
  const key = keyMap[status];
  return key ? t(`pda.GlobalSearchPage.bagStatus.${key}`) : String(status);
};

// ISO-8601 → yyyy/MM/dd HH:mm:ss；解析失败或空返回 '-'
const formatIsoDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const PageBody = () => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');
  const [currentOrder, setCurrentOrder] = useState<any>(undefined);
  const [currentBag, setCurrentBag] = useState<any>(undefined);
  const [currentOrderDetail, setCurrentOrderDetail] = useState<any>(undefined);
  const [exceptionDictMap, setExceptionDictMap] = useState<Map<string, any>>(new Map());
  const [channelMap, setChannelMap] = useState<Map<string, any>>(new Map());
  const [exceptionImagesVisible, setExceptionImagesVisible] = useState(false);
  const [podImagesVisible, setPodImagesVisible] = useState(false);
  // 包牌查询降级开关：true 时 PDA 仅支持运单号（getConfigByKey 拉不到 / config 关闭 / configValue!=='false' 都视为 true）
  const bagQueryDegradeEnabled = useBagQueryDegradeEnabled();
  // 轨迹事件图片查看器（与异常照片按钮独立；点击某张图打开对应轮播索引）
  const [eventImages, setEventImages] = useState<string[]>([]);
  const [eventImagesIndex, setEventImagesIndex] = useState(0);
  const [eventImagesVisible, setEventImagesVisible] = useState(false);
  const openEventImages = (images: string[], index: number) => {
    setEventImages(images);
    setEventImagesIndex(index);
    setEventImagesVisible(true);
  };

  const [fetchOrderOrBagByScan, { loading }] = useLazyQuery(GET_ORDER_OR_BAG_BY_SCAN_INPUT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const wrapper = data?.getOrderOrBagByScanInput || null;
      const orderDetail = wrapper?.getOrderByScanInput || null;   // 完整 OrderDetail 结构
      const bag = wrapper?.getBagByScanInput || null;
      // 从 detail 中提取上层 order（保持 currentOrder 兼容旧消费点）
      const order = orderDetail?.orderWithAddresses?.order || null;
      setCurrentOrder(order);
      setCurrentOrderDetail(orderDetail);
      setCurrentBag(bag);

      // 两分支都为空 → 未找到
      if (!order && !bag) {
        Toast.show({
          content: t('errors.business.order.notExist'),
          position: 'bottom'
        });
      }
    },
    onError: (error) => {
      Toast.show({
        content: t('errors.business.order.notExist'),
        position: 'bottom'
      });
    }
  });
  
  // Channel查询hook
  const [fetchChannel] = useLazyQuery(GET_CHANNEL, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.getChannel) {
        const channel = data.getChannel;
        setChannelMap(prev => new Map(prev).set(channel.id, channel));
      }
    },
    onError: (error) => {
    }
  });
  const [fetchExceptionDict] = useLazyQuery(GET_EXCEPTION_DICTIONARY_BY_REASON_CODE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const dict = data?.getExceptionDictionaryByReasonCode;
      if (dict && dict.exceptionReasonCode) {
        setExceptionDictMap(prev => new Map(prev.set(dict.exceptionReasonCode, dict)));
      }
    }
  });

  const trackingNumber = currentOrder?.trackingNumber || '';
  const status = currentOrder?.overallStatus || '';

  // 格式化状态：使用国际化
  const formatStatus = (status: string) => {
    if (!status) return '';
    
    // 尝试获取国际化翻译
    const i18nKey = `enum.overallStatus.${status}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回原始状态
    return translated === i18nKey ? status : translated;
  };

  // 格式化下一步操作：使用国际化
  const formatNextAction = (nextAction: string) => {
    if (!nextAction) return '';
    
    // 直接使用原始格式匹配配置
    const i18nKey = `config.nextAction.${nextAction}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回原始值
    return translated === i18nKey ? nextAction : translated;
  };

  // 分拣段码展示：直接取 order.sortingCode 字段（不再走 realTimeSortingCode 兜底）
  const sortingCodeCombined = useMemo(() => {
    const sc = currentOrder?.sortingCode;
    return sc && sc.length > 0 ? sc : '-';
  }, [currentOrder]);

  // 所有事件：来自 orderDetail.orderEvents，后端按 event ID 升序返回，翻转为倒序展示
  const allEvents = useMemo(() => {
    const events = currentOrderDetail?.orderEvents || [];
    if (events.length === 0) return [];
    return [...events].reverse();
  }, [currentOrderDetail]);

  // 格式化时间：将秒时间戳转换为可读格式
  const formatTime = (time: number) => {
    if (!time || Number.isNaN(time)) return '-';
    const d = new Date(time * 1000);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // 格式化事件标题：使用国际化
  const formatEventTitle = (type: string) => {
    if (!type) return '-';
    
    // 去掉 "OrderEventData" 前缀，获取实际的事件类型
    const key = 'OrderEventData';
    const idx = type.indexOf(key);
    let eventType = type;
    if (idx >= 0) {
      eventType = type.substring(idx + key.length).replace(/^[:\s-]+/, '') || type;
    }
    
    // 尝试获取国际化翻译，如果没有找到则返回原始类型
    const i18nKey = `enum.orderEvent.${eventType}`;
    const translated = t(i18nKey);
    
    // 如果翻译结果与key相同，说明没有找到翻译，返回处理后的原始类型
    return translated === i18nKey ? eventType : translated;
  };

  // 获取 by 信息：从 details 中以 "by " 开头的项，对于null-null情况显示channelName+版本号
  const getByInfo = (event: any) => {
    // 先从 details 中获取 by 信息
    const details = event.details || [];
    const byItem = details.find((line: string) => /^by\s+/i.test(line));
    const byValue = byItem ? byItem.replace(/^by\s+/i, '') : '';
    
    // 如果 by 值是 null-null 格式，使用渠道信息
    if (byValue === 'null-null' || 
        byValue.trim() === '' || 
        /^null[\-\s]*null$/i.test(byValue)) {
      
      const channelId = currentOrderDetail?.orderWithAddresses?.order?.channelId;
      const channelApiVersion = event.channelApiVersion;
      
      if (channelId && channelApiVersion) {
        const channel = channelMap.get(channelId.toString());
        if (channel) {
          // 显示为 channelName-V{version} 格式
          return `${channel.channelName}-V${channelApiVersion}`;
        }
      }
      
      // 如果没有渠道信息，返回系统
      return '系统';
    }
    
    // 否则返回原始的 by 值
    return byValue || '-';
  };

  // 查询异常字典：为所有不同的异常原因代码查询字典（事件级 + 订单级 exceptionReasonCode）
  useEffect(() => {
    const uniqueReasonCodes = new Set<string>();

    allEvents.forEach(event => {
      if (event.exceptionReasonCode &&
          typeof event.exceptionReasonCode === 'string' &&
          event.exceptionReasonCode.trim().length > 0) {
        uniqueReasonCodes.add(event.exceptionReasonCode);
      }
    });
    // 订单级异常原因（详情 Tab "异常原因" 行使用）
    const orderReason = currentOrderDetail?.orderWithAddresses?.order?.exceptionReasonCode;
    if (typeof orderReason === 'string' && orderReason.trim().length > 0) {
      uniqueReasonCodes.add(orderReason);
    }

    // 查询还没有缓存的异常字典
    uniqueReasonCodes.forEach(code => {
      if (!exceptionDictMap.has(code)) {
        fetchExceptionDict({ variables: { exceptionReasonCode: code } });
      }
    });
  }, [allEvents, exceptionDictMap, currentOrderDetail]);

  // 按当前语言从异常字典拿具体原因；无字典条目时回退原始 code
  const formatExceptionReason = useCallback((reasonCode: string | null | undefined): string => {
    if (!reasonCode) return '-';
    const dict = exceptionDictMap.get(reasonCode);
    if (!dict) return reasonCode;
    const lang = i18n.language || 'en';
    if (lang.startsWith('zh')) return dict.secondaryTypeZh || dict.secondaryTypeEn || reasonCode;
    if (lang.startsWith('es')) return dict.secondaryTypeEs || dict.secondaryTypeEn || reasonCode;
    return dict.secondaryTypeEn || dict.secondaryTypeZh || reasonCode;
  }, [exceptionDictMap]);

  // 查询Channel信息：当有create状态事件时自动查询
  useEffect(() => {
    const createEvents = allEvents.filter(event => 
      event.type === 'OrderEventDataCreated'
    );
    
    if (createEvents.length > 0 && currentOrderDetail?.orderWithAddresses?.order?.channelId) {
      const orderChannelId = currentOrderDetail.orderWithAddresses.order.channelId;
      if (!channelMap.has(orderChannelId.toString())) {
        fetchChannel({ variables: { id: orderChannelId } });
      }
    }
  }, [allEvents, currentOrderDetail, channelMap]);

  // 获取显示用的失败原因：优先使用对应的异常字典，其次使用 failReason
  const getDisplayReason = (event: any) => {
    // 如果有异常原因代码且查询到了对应的异常字典，根据当前语言选择对应字段
    if (event.exceptionReasonCode && exceptionDictMap.has(event.exceptionReasonCode)) {
      const exceptionDict = exceptionDictMap.get(event.exceptionReasonCode);
      const currentLang = i18n.language || 'en';
      
      // 根据语言选择对应字段 - 支持更多中文语言代码
      if (currentLang.startsWith('zh')) {
        return exceptionDict.secondaryTypeZh || exceptionDict.secondaryTypeEn || '-';
      } else if (currentLang.startsWith('es')) {
        return exceptionDict.secondaryTypeEs || exceptionDict.secondaryTypeEn || '-';
      } else {
        // 默认英文
        return exceptionDict.secondaryTypeEn || exceptionDict.secondaryTypeZh || '-';
      }
    }
    // LogScan 事件使用 messageKey 国际化翻译
    if (event.messageKey) {
      const messageTranslated = t(`${event.messageKey}.message`, event.messageKey);
      if (messageTranslated !== event.messageKey) return messageTranslated;
      const translated = t(event.messageKey);
      if (typeof translated === 'string' && translated !== event.messageKey) return translated;
      // 两次翻译都未命中（多语言漏配）：兜底显示 failReason 或原始 key，
      // 不能落到 '-'——渲染处会因 displayReason === '-' 把整行隐藏，导致操作员完全看不到失败原因
      return event.failReason || event.messageKey;
    }
    // 否则使用 failReason
    return event.failReason || '-';
  };

  // 缓存每个事件的显示原因，避免重复计算
  const eventsWithDisplayReason = useMemo(() => {
    return allEvents.map(event => ({
      ...event,
      displayReason: getDisplayReason(event)
    }));
  }, [allEvents, exceptionDictMap]);

  const onProcess = useCallback(async (event: PdaScanEvent) => {
    const code = event.code;
    if (!isValidBarCode(code)) {
      showBarCodeError(code);
      return;
    }
    // 降级：只支持运单查询，扫到包牌号（BAG / TT 前缀）时提示不发请求
    if (bagQueryDegradeEnabled && (isBagNumber(code) || isTtBagNumber(code))) {
      Toast.show({ content: t('errors.business.order.notExist'), position: 'bottom' });
      return;
    }
    setKeyword(code);
    const v = code.trim();
    if (v) {
      // 每次搜索先清空上一次结果，避免展示旧数据
      setCurrentOrder(undefined);
      setCurrentBag(undefined);
      setCurrentOrderDetail(undefined);
      setExceptionDictMap(new Map());
      await fetchOrderOrBagByScan({
        variables: { scanValue: v }
      });
    }
  }, [fetchOrderOrBagByScan, bagQueryDegradeEnabled, t]);

  useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const onSearch = async () => {
    const v = keyword.trim();
    if (!v) {
      Toast.show({ content: t('pda.GlobalSearchPage.inputPlaceholder'), position: 'bottom' });
      return;
    }
    if (!isValidBarCode(v)) {
      showBarCodeError(v);
      return;
    }
    // 降级：只支持运单查询
    if (bagQueryDegradeEnabled && (isBagNumber(v) || isTtBagNumber(v))) {
      Toast.show({ content: t('errors.business.order.notExist'), position: 'bottom' });
      return;
    }

    try {
      // 每次搜索先清空上一次结果，避免展示旧数据
      setCurrentOrder(undefined);
      setCurrentBag(undefined);
      setCurrentOrderDetail(undefined);
      setExceptionDictMap(new Map());
      await fetchOrderOrBagByScan({
        variables: { scanValue: v }
      });
    } catch (e) {
      Toast.show({ content: t('pda.GlobalSearchPage.searchFailed'), position: 'bottom' });
    }
  };

  return (
    <>
      {/* 顶部输入框（带扫描图标）*/}
      <div className="flex items-center mb-2 border rounded">
        <Input
          className="flex-1 p-2"
          placeholder={t('pda.GlobalSearchPage.placeholder') || 'SWX123456789'}
          value={keyword}
          onChange={setKeyword}
          onEnterPress={onSearch}
        />
        <Button
          fill={"none"}
          className="ml-2 bg-transparent border-0"
          onClick={onSearch}
        >
          <ScanningOutline fontSize={24} />
        </Button>
      </div>

      {/* 包牌结果：Tabs（详情 / 发运历史） */}
      {currentBag && (
        <Tabs>
          <Tabs.Tab title={t('pda.GlobalSearchPage.bagTab.detail')} key="detail">
            <div className="px-3 py-3 text-sm">
              <table className="w-full border-collapse">
                <tbody>
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.bagNumber')} value={currentBag.bagNumber} />
                    {currentBag.businessType === 2 ? (
                      <>
                        {/* TT 包牌：第一分拣规则 + 预分拣码拼接（promptSortingRule，如 LAX1-ATL1-SWX_HOU） */}
                        <BagDetailRow
                          label={t('pda.GlobalSearchPage.bagDetail.ttSortingRule')}
                          value={(currentBag.promptSortingRule || currentBag.sortingRule) || '-'}
                        />
                        {/* SWX 本地派送尾程码（bag.sorting_code） */}
                        <BagDetailRow
                          label={t('pda.GlobalSearchPage.bagDetail.swxLmSortingCode')}
                          value={currentBag.sortingCode || '-'}
                        />
                      </>
                    ) : (
                      <BagDetailRow
                        label={t('pda.GlobalSearchPage.bagDetail.sortingCode')}
                        value={currentBag.sortingCode || '-'}
                      />
                    )}
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.status')} value={formatBagStatus(currentBag.status, t)} />
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.creationWarehouse')} value={currentBag.creationWarehouseName || '-'} />
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.createdAt')} value={formatIsoDateTime(currentBag.createdAt)} />
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.pastParcelCount')} value={String(currentBag.pastParcelCount ?? 0)} />
                    <BagDetailRow label={t('pda.GlobalSearchPage.bagDetail.currentParcelCount')} value={String(currentBag.currentParcelCount ?? 0)} />
                  </tbody>
                </table>
              </div>
            </Tabs.Tab>
          <Tabs.Tab title={t('pda.GlobalSearchPage.bagTab.history')} key="history">
            <div className="px-3 py-3">
              {(!currentBag.shipmentHistory || currentBag.shipmentHistory.length === 0) ? (
                <div className="text-sm text-gray-500 text-center py-4">
                  {t('pda.GlobalSearchPage.bagHistory.empty')}
                </div>
              ) : (
                <div>
                  {[...currentBag.shipmentHistory]
                    .sort((a: any, b: any) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
                    .map((node: any, idx: number, arr: any[]) => {
                    const dotColor = node.kind === 'CREATED' ? 'bg-blue-500'
                      : node.kind === 'DEPARTURE_SCAN' ? 'bg-green-500'
                      : node.kind === 'BAG_RESHIP' ? 'bg-orange-400'
                      : node.kind === 'LOG_SCAN' ? 'bg-red-400'
                      : 'bg-gray-400';
                    const isLast = idx === arr.length - 1;
                    return (
                      <div key={node.id} className="flex">
                        {/* 左列：圆点 + 竖线 */}
                        <div className="flex flex-col items-center mr-3">
                          <div className={`w-3 h-3 rounded-full ${dotColor} mt-1.5 shrink-0`}></div>
                          {!isLast && <div className="w-0.5 flex-1 bg-gray-200 my-1"></div>}
                        </div>
                        {/* 右列：节点内容 */}
                        <div className={`flex-1 ${isLast ? '' : 'pb-4'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-bold">{t(`pda.GlobalSearchPage.bagHistory.nodeKind.${node.kind}`)}</span>
                            <span className="text-sm text-gray-700">{formatIsoDateTime(node.occurredAt)}</span>
                          </div>
                          {node.parcelCount !== null && node.parcelCount !== undefined && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.parcelCount')}: {node.parcelCount}</div>
                          )}
                          {node.operatorName && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.operator')}: {node.operatorName}</div>
                          )}
                          {node.operatorRole && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.operatorRole')}: {node.operatorRole}</div>
                          )}
                          {node.warehouseName && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.warehouse')}: {node.warehouseName}</div>
                          )}
                          {node.originalScanType && (
                            <div className="text-sm text-gray-700">
                              {t('pda.GlobalSearchPage.bagHistory.originalScanType')}: {t(`enum.orderEvent.${node.originalScanType}`, { defaultValue: node.originalScanType })}
                            </div>
                          )}
                          {node.oldSortingRule && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.oldSortingRule')}: {node.oldSortingRule}</div>
                          )}
                          {node.newSortingRule && (
                            <div className="text-sm text-gray-700">{t('pda.GlobalSearchPage.bagHistory.newSortingRule')}: {node.newSortingRule}</div>
                          )}
                          {node.failMessageKey && (
                            <div className="text-sm text-red-500">
                              {t('pda.GlobalSearchPage.bagHistory.failReason')}: {t(node.failMessageKey, { defaultValue: node.failMessageKey })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Tabs.Tab>
        </Tabs>
      )}

      {/* 包裹结果：Tabs（详情 / 轨迹） */}
      {currentOrder && (() => {
        const detailOrder = currentOrderDetail?.orderWithAddresses?.order;
        const orderWms = currentOrderDetail?.orderWms;
        const deliveryInfo = currentOrderDetail?.deliveryInfo;
        const fmt = (v: any): string => (v === null || v === undefined || v === '' ? '-' : String(v));
        return (
        <Tabs>
          <Tabs.Tab title={t('pda.GlobalSearchPage.orderTab.detail')} key="orderDetail">
            <div className="px-3 py-3 text-sm">
              <table className="w-full border-collapse">
                <tbody>
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.trackingNumber')} value={fmt(trackingNumber)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.sortingCode')} value={fmt(sortingCodeCombined)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.ttSortingRule')} value={fmt(orderWms?.sortingRule)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.ttSortingCode')} value={fmt(orderWms?.ttSortingCode)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.temuSortingCode')} value={fmt(orderWms?.temuSortingCode)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.returnSortingCode')} value={fmt(detailOrder?.returnSortingCode)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.batchNumber')} value={fmt(currentOrderDetail?.bagBatchNumber)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.overallStatus')} value={fmt(formatStatus(status))} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.exceptionReason')} value={formatExceptionReason(detailOrder?.exceptionReasonCode)} wrap />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.nextAction')} value={fmt(formatNextAction(detailOrder?.nextAction || ''))} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.deliveryAttempts')} value={fmt(detailOrder?.deliveryAttempts)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.taskDate')} value={fmt(deliveryInfo?.taskDate)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.routeName')} value={fmt(deliveryInfo?.routeName)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.stopNumber')} value={fmt(deliveryInfo?.stopNumber)} />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.driver')} value={fmt(deliveryInfo?.driverName)} wrap />
                  <BagDetailRow label={t('pda.GlobalSearchPage.orderDetail.dsp')} value={fmt(detailOrder?.dsp?.name)} wrap />
                </tbody>
              </table>

              {/* 详情底部操作：查看照片按钮（90% 宽，浅灰底） */}
              <div className="mt-3 flex flex-col gap-2 items-center">
                {detailOrder?.podImageS3UrlsJson && detailOrder.podImageS3UrlsJson.length > 0 && (
                  <Button
                    size="small"
                    fill="solid"
                    color="default"
                    className="!bg-gray-200"
                    style={{ width: '90%' }}
                    onClick={() => setPodImagesVisible(true)}
                  >
                    {t('pda.GlobalSearchPage.orderDetail.viewPodPhotos')}
                  </Button>
                )}
                {(detailOrder?.isAbnormal || ['RETURN_STARTED', 'RETURN_ARRIVED_DESTINATION_HUB'].includes(detailOrder?.overallStatus)) &&
                 detailOrder?.exceptionImageS3UrlsJson && detailOrder.exceptionImageS3UrlsJson.length > 0 && (
                  <Button
                    size="small"
                    fill="solid"
                    color="default"
                    className="!bg-gray-200"
                    style={{ width: '90%' }}
                    onClick={() => setExceptionImagesVisible(true)}
                  >
                    {t('pda.GlobalSearchPage.orderDetail.viewExceptionPhotos')}
                  </Button>
                )}
              </div>
            </div>
          </Tabs.Tab>
          <Tabs.Tab title={t('pda.GlobalSearchPage.orderTab.events')} key="orderEvents">
            <div className="px-3 py-3">
            {allEvents.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                {t('pda.pages.GlobalSearchPage.noEvents') || '暂无事件记录'}
              </div>
            ) : (
              <div>
                {eventsWithDisplayReason.map((event, index, arr) => {
                  const isLast = index === arr.length - 1;
                  return (
                  <div key={index} className="flex">
                    {/* 左列：圆点 + 竖线 */}
                    <div className="flex flex-col items-center mr-3">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                      {!isLast && <div className="w-0.5 flex-1 bg-gray-200 my-1"></div>}
                    </div>
                    {/* 右列：事件内容 */}
                    <div className={`flex-1 ${isLast ? '' : 'pb-4'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold">{formatEventTitle(event.type)}</span>
                      <span className="text-sm text-gray-700">{formatTime(Number(event.time))}</span>
                    </div>
                    {/* 操作人信息 —— 有 operatorName 等就展示；否则 fallback 到 by info */}
                    {(event.operatorName || event.operatorPhone || event.operatorRole || event.operatorWarehouseName || event.operatorDspName) ? (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.operatorName && (<div>{t('components.timeline.operatorName', '操作人')}: {event.operatorName}</div>)}
                        {event.operatorPhone && (<div>{t('components.timeline.operatorPhone', '操作人电话')}: {event.operatorPhone}</div>)}
                        {event.operatorRole && (<div>{t('components.timeline.operatorRole', '操作人角色')}: {event.operatorRole}</div>)}
                        {event.operatorWarehouseName && (<div>{t('components.timeline.operatorWarehouseName', '操作站点')}: {event.operatorWarehouseName}</div>)}
                        {event.operatorDspName && (<div>{t('components.timeline.operatorDspName', '操作DSP')}: {event.operatorDspName}</div>)}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 mb-1">
                        {t('pda.pages.GlobalSearchPage.by')} {getByInfo(event)}
                      </div>
                    )}

                    {/* 司机信息 */}
                    {(event.driverName || event.driverPhone || event.dspName) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.driverName && (<div>{t('components.timeline.driverName', '司机')}: {event.driverName}</div>)}
                        {event.driverPhone && (<div>{t('components.timeline.driverPhone', '司机电话')}: {event.driverPhone}</div>)}
                        {event.dspName && (<div>{t('components.timeline.dspName', 'DSP')}: {event.dspName}</div>)}
                      </div>
                    )}

                    {/* 承运商 & 车辆 */}
                    {(event.carrierName || event.licensePlate || event.sealNo) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.carrierName && (<div>{t('components.timeline.carrierName', '承运商')}: {event.carrierName}</div>)}
                        {event.licensePlate && (<div>{t('components.timeline.licensePlate', '车牌')}: {event.licensePlate}</div>)}
                        {event.sealNo && (<div>{t('components.timeline.sealNo', '封条')}: {event.sealNo}</div>)}
                      </div>
                    )}

                    {/* 站点 */}
                    {(event.currentWarehouseName || event.pickupHubName) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.currentWarehouseName && (<div>{t('components.timeline.currentWarehouseName', '当前站点')}: {event.currentWarehouseName}</div>)}
                        {event.pickupHubName && (<div>{t('components.timeline.pickupHubName', '揽收站点')}: {event.pickupHubName}</div>)}
                      </div>
                    )}

                    {/* 包牌相关 */}
                    {(event.bagNumber || event.bagSortingCode || event.scanSource || event.bagTaskName || event.clientCartonNo || event.fromBagNumber || event.toBagNumber || event.oldSortingRule || event.newSortingRule) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.bagNumber && (<div>{t('components.timeline.bagNumber', '包牌号')}: {event.bagNumber}</div>)}
                        {event.bagSortingCode && (<div>{t('components.timeline.bagSortingCode', '包牌分拣码')}: {event.bagSortingCode}</div>)}
                        {event.scanSource && (<div>{`${t('components.timeline.scanSource', '扫描来源')}: ${t(`components.timeline.scanSourceNames.${event.scanSource}`, event.scanSource)}`}</div>)}
                        {event.bagTaskName && (<div>{t('components.timeline.bagTaskName', '组包任务')}: {event.bagTaskName}</div>)}
                        {event.clientCartonNo && (<div>{t('components.timeline.clientCartonNo', '客户大包号')}: {event.clientCartonNo}</div>)}
                        {event.presortCode && (<div>{t('components.timeline.presortCode', '预分拣码')}: {event.presortCode}</div>)}
                        {event.originalScanAction && (<div>{`${t('components.timeline.originalScanAction', '原始扫描动作')}: ${t(`components.timeline.originalScanActionNames.${event.originalScanAction}`, event.originalScanAction)}`}</div>)}
                        {event.fromBagNumber && (<div>{t('components.timeline.fromBagNumber', '源包牌')}: {event.fromBagNumber}</div>)}
                        {event.toBagNumber && (<div>{t('components.timeline.toBagNumber', '目标包牌')}: {event.toBagNumber}</div>)}
                        {event.oldSortingRule && (<div>{t('components.timeline.oldSortingRule', '包牌原分拣规则')}: {event.oldSortingRule}</div>)}
                        {event.newSortingRule && (<div>{t('components.timeline.newSortingRule', '包牌新分拣规则')}: {event.newSortingRule}</div>)}
                      </div>
                    )}

                    {/* 分拣设备 & 重量 */}
                    {(event.sortingMachineCode || event.weightKg != null) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.sortingMachineCode && (<div>{t('components.timeline.sortingMachineCode', '分拣设备')}: {event.sortingMachineCode}</div>)}
                        {event.weightKg != null && (<div>{t('components.timeline.weight', '重量(kg)')}: {event.weightKg}</div>)}
                      </div>
                    )}

                    {/* 派送信息 */}
                    {(event.deliveryAttempts != null || event.deliveryDistance != null || (event.latitude != null && event.longitude != null) || event.deliveryMethod) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.deliveryAttempts != null && (<div>{t('components.timeline.deliveryAttempts', '尝试次数')}: {event.deliveryAttempts}</div>)}
                        {event.deliveryDistance != null && (<div>{t('components.timeline.deliveryDistance', { distance: event.deliveryDistance.toFixed(2) })}</div>)}
                        {event.latitude != null && event.longitude != null && (
                          <div>{t('components.timeline.deliveryLocation', '派送位置')}: {event.latitude === 0 && event.longitude === 0 ? '0,0' : `${event.latitude.toFixed(7)}, ${event.longitude.toFixed(7)}`}</div>
                        )}
                        {event.deliveryMethod && (<div>{t('components.timeline.deliveryMethod', '妥投方式')}: {event.deliveryMethod}</div>)}
                      </div>
                    )}

                    {/* SC 阶段 & 目的地标记 */}
                    {(event.isDestHubArrived === true || event.isFirstLmArrival || (event.scStages && event.scStages.length > 0)) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.isDestHubArrived === true && (
                          <div>{t('pda.pages.GlobalSearchPage.isDestHubArrived')}: {t('pda.common.yes')}</div>
                        )}
                        {event.scStages && event.scStages.length > 0 && (
                          <div>{t('pda.pages.GlobalSearchPage.scStages')}: {event.scStages.map((s: string) => t(`pda.pages.GlobalSearchPage.scStageNames.${s}`, s)).join(', ')}</div>
                        )}
                        {event.isFirstLmArrival && (
                          <div>{t('pda.pages.GlobalSearchPage.isFirstLmArrival')}: {t('pda.common.yes')}</div>
                        )}
                      </div>
                    )}

                    {/* 预约揽收单号 */}
                    {event.appointmentNumber && (
                      <div className="text-sm text-gray-500 mb-1">
                        {t('components.timeline.appointmentNumber', '预约单号')}: {event.appointmentNumber}
                      </div>
                    )}

                    {/* LogScan 原始事件类型 & notes */}
                    {event.originalEventType && (
                      <div className="text-sm text-gray-500 mb-1">
                        {t('pda.pages.GlobalSearchPage.originalEventType')}: {formatEventTitle(event.originalEventType)}
                      </div>
                    )}
                    {event.notes && (
                      <div className="text-sm text-gray-500 mb-1 whitespace-pre-wrap break-all">
                        {t('components.timeline.logScanDetails', '详情')}: {event.notes}
                      </div>
                    )}

                    {/* 撤回原因 / 发车事件类型 / 事件原因（地址改写等） */}
                    {event.revokeReason && (
                      <div className="text-sm text-gray-500 mb-1">
                        {t('components.timeline.revokeReasonLabel', '撤回原因')}: {event.revokeReason}
                      </div>
                    )}
                    {event.departureEventType != null && (
                      <div className="text-sm text-gray-500 mb-1">
                        {/* 后端返回 int（1=LINEHAUL_ORIGIN_HUB_DEPARTURE / 2=OTHER_DEPARTURE / 3=FEEDER_DEST_HUB_DEPARTURE / 4=HANDOVER），
                            前端映射为 enum name 再走 i18n key components.timeline.departureEventTypeValue.{enumName}，与 desktop-web 一致 */}
                        {t('components.timeline.departureEventType', '发车类型')}: {(() => {
                          const nameByValue: Record<number, string> = {
                            1: 'LINEHAUL_ORIGIN_HUB_DEPARTURE',
                            2: 'OTHER_DEPARTURE',
                            3: 'FEEDER_DEST_HUB_DEPARTURE',
                            4: 'HANDOVER',
                          };
                          const enumName = nameByValue[event.departureEventType];
                          return enumName
                            ? String(t(`components.timeline.departureEventTypeValue.${enumName}`, enumName))
                            : String(event.departureEventType);
                        })()}
                      </div>
                    )}
                    {event.reason && (
                      <div className="text-sm text-gray-500 mb-1">
                        {t('pda.pages.GlobalSearchPage.reason')}: {event.reason}
                      </div>
                    )}

                    {/* 通用 reason / nextAction —— created 事件不显示 */}
                    {event.type !== 'OrderEventDataCreated' && (
                      <>
                        {event.displayReason && event.displayReason !== '-' && (
                          <div className="text-sm text-gray-500 mb-1">{t('pda.pages.GlobalSearchPage.reason')}: {event.displayReason}</div>
                        )}
                        {event.nextAction && event.nextAction !== '-' && (
                          <div className="text-sm text-gray-500">{t('pda.pages.GlobalSearchPage.nextAction')}: {formatNextAction(event.nextAction)}</div>
                        )}
                      </>
                    )}

                    {/* 地址变更事件（对齐 desktop-web CustomParcelsTimeline）：
                        仅 OrderEventDataRecipientAddressChanged 类型展示；
                        formatAddress 拼多行 addressLine1 / addressLine2 / city, state, zipcode, US；
                        reason 通过 components.timeline.reasonValues 二次翻译，缺失时回退原始值 */}
                    {event.type === 'OrderEventDataRecipientAddressChanged' && (event.oldRecipientAddress || event.newRecipientAddress) && (
                      <div className="mt-2 mb-1 p-2 bg-gray-50 rounded-md border-l-4 border-blue-500">
                        <div className="text-sm space-y-2">
                          {event.oldRecipientAddress && (
                            <div>
                              <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldAddress', '修改前地址')}:</div>
                              <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border break-all">
                                {formatAddress(event.oldRecipientAddress)}
                              </div>
                            </div>
                          )}
                          {event.newRecipientAddress && (
                            <div>
                              <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newAddress', '修改后地址')}:</div>
                              <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border break-all">
                                {formatAddress(event.newRecipientAddress)}
                              </div>
                            </div>
                          )}
                          {event.reason && (
                            <div>
                              <span className="font-medium text-gray-700">{t('components.timeline.reason', '原因')}:</span>
                              <span className="text-gray-600 ml-1">{String(t(`components.timeline.reasonValues.${event.reason}`, event.reason))}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* POD 审核 */}
                    {(event.previousStatus || event.newStatus) && (
                      <div className="text-sm text-gray-500 mb-1 space-y-1">
                        {event.previousStatus && (<div>{t('components.timeline.previousStatus', '原审核状态')}: {event.previousStatus}</div>)}
                        {event.newStatus && (<div>{t('components.timeline.currentStatus', '新审核状态')}: {event.newStatus}</div>)}
                        {event.podComment && event.podComment.length > 0 && (<div>{t('components.timeline.podComment', 'POD 备注')}: {event.podComment.join(', ')}</div>)}
                      </div>
                    )}

                    {/* 照片 grid：点击缩略图打开 ImageViewer.Multi 全屏轮播，defaultIndex 定位到当前图 */}
                    {event.imageS3UrlsJson && event.imageS3UrlsJson.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {event.imageS3UrlsJson.map((url: string, i: number) => (
                          <img
                            key={i}
                            src={url}
                            alt=""
                            className="w-16 h-16 object-cover rounded border border-gray-300 cursor-pointer active:opacity-70"
                            onClick={() => openEventImages(event.imageS3UrlsJson, i)}
                          />
                        ))}
                      </div>
                    )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            </div>
          </Tabs.Tab>
        </Tabs>
        );
      })()}

      {/* 加载提示 */}
      {loading && (
        <div className="py-2 text-sm text-gray-500">{t('pda.GlobalSearchPage.loading')}</div>
      )}

      {/* 异常照片查看器（多图轮播） */}
      <ImageViewer.Multi
        images={currentOrderDetail?.orderWithAddresses?.order?.exceptionImageS3UrlsJson || []}
        visible={exceptionImagesVisible}
        defaultIndex={0}
        onClose={() => setExceptionImagesVisible(false)}
      />
      {/* 妥投照片查看器（多图轮播） */}
      <ImageViewer.Multi
        images={currentOrderDetail?.orderWithAddresses?.order?.podImageS3UrlsJson || []}
        visible={podImagesVisible}
        defaultIndex={0}
        onClose={() => setPodImagesVisible(false)}
      />
      {/* 轨迹事件图片查看器（多图轮播），index 由点击的缩略图决定 */}
      <ImageViewer.Multi
        images={eventImages}
        visible={eventImagesVisible}
        defaultIndex={eventImagesIndex}
        onClose={() => setEventImagesVisible(false)}
      />
    </>
  );
};

export default function PdaGlobalSearchPage() {
  const { t } = useTranslation();
  
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2">
      <Header title={t('pda.GlobalSearchPage.title')} />
      <Pda />
      <PageBody />
    </div>
  );
}